import { useEffect, useState } from 'preact/hooks'
import { Mail, Trash2, UserPlus, Users } from 'lucide-preact'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import { currentMembership } from '../auth/household'
import { isSyncConfigured } from '../auth/supabase'
import { readMemberStatus, sendInvitation, type MemberStatus } from '../lib/invite'
import { live, useAppStore } from '../store/store'
import { MemberForm } from './MemberForm'

/**
 * *Le foyer*: who lives here, and how they get in.
 *
 * Everybody sees the list — the day list needs their names and a chore assigned
 * to somebody has to be able to say to whom. Only the household's owner sees the
 * controls, and that is presentation only: the database refuses a write from
 * anybody else through the policy `0006` installs, so a client that rendered the
 * buttons anyway would render them and then be told no. Enforcing it here as
 * well is a courtesy to the person holding the phone, not a security boundary —
 * `CLAUDE.md` is explicit that a rule kept in a bundle that ships publicly is
 * not a rule.
 *
 * The two badges — *Compte actif*, *Invitation envoyée* — come from a direct
 * read rather than from the synced row, because neither `user_id` nor
 * `member_invites` crosses the sync engine and both are deliberately outside it.
 * Offline that read fails and the list renders names with no badges, which is
 * the right degradation: a name with no badge is incomplete, a badge that is
 * stale is wrong.
 */
export function HouseholdCard() {
  const members = useAppStore((s) => s.members)
  const addMember = useAppStore((s) => s.addMember)
  const removeMember = useAppStore((s) => s.removeMember)

  const [adding, setAdding] = useState(false)
  const [notice, setNotice] = useState('')
  const [busy, setBusy] = useState('')
  const [status, setStatus] = useState<Map<string, MemberStatus>>(new Map())

  const membership = currentMembership()
  const selfMemberId = membership?.memberId ?? ''
  // With no backend there is no household to own, and the person holding the
  // phone is the only one there is. That is also the mode the UI is developed
  // in, so hiding every control behind an owner check would hide the feature.
  const canManage = !isSyncConfigured || membership?.isOwner === true

  const householdId = membership?.householdId ?? ''
  const people = live(members)

  // Re-read whenever the list changes, so a member who has just redeemed stops
  // reading "Invitation envoyée" without anybody having to reload the app.
  useEffect(() => {
    if (!householdId) return
    let cancelled = false

    void readMemberStatus(householdId)
      .then((next) => {
        if (!cancelled) setStatus(next)
      })
      .catch(() => {
        // Offline, or a read the policies refuse. The list still renders; it
        // just says less. Nothing here is worth interrupting Réglages for.
        if (!cancelled) setStatus(new Map())
      })

    return () => {
      cancelled = true
    }
  }, [householdId, people.length])

  async function invite(memberId: string, name: string) {
    setBusy(memberId)
    setNotice('')
    try {
      await sendInvitation(memberId)
      setNotice(`Invitation envoyée à ${name} ✉️`)
      if (householdId) setStatus(await readMemberStatus(householdId))
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Envoi impossible.')
    } finally {
      setBusy('')
    }
  }

  function describe(memberId: string, hasLogin: boolean): string {
    const state = status.get(memberId)
    if (state?.hasAccount) return 'Compte actif'
    if (state?.invitedAt) {
      const sent = new Date(state.invitedAt)
      return Number.isNaN(sent.getTime())
        ? 'Invitation envoyée'
        : `Invitation envoyée le ${format(sent, 'd MMMM', { locale: fr })}`
    }
    return hasLogin ? 'Invitation à envoyer' : 'Sans compte'
  }

  return (
    <>
      <div class="section-heading">
        <h2>Le foyer</h2>
        <Users size={20} />
      </div>

      {people.length === 0 ? (
        <p class="notice muted">
          Tu es seul·e dans ce foyer pour l’instant. Ajoute quelqu’un pour lui attribuer des
          tâches.
        </p>
      ) : (
        <div class="custom-task-list">
          {people.map((member) => (
            <div class="custom-task-row">
              <span class="task-emoji">{member.displayName.slice(0, 1).toUpperCase()}</span>
              <span class="custom-task-text">
                <strong>
                  {member.displayName}
                  {member.id === selfMemberId && <span class="member-you"> toi</span>}
                </strong>
                <small>{describe(member.id, Boolean(member.authEmail))}</small>
              </span>

              {canManage && member.authEmail && !status.get(member.id)?.hasAccount && (
                <button
                  class="icon-button small"
                  disabled={busy === member.id}
                  onClick={() => void invite(member.id, member.displayName)}
                  aria-label={`Envoyer l’invitation à ${member.displayName}`}
                >
                  <Mail size={17} />
                </button>
              )}

              {canManage && member.id !== selfMemberId && (
                <button
                  class="icon-button small danger"
                  onClick={() => removeMember(member.id, selfMemberId)}
                  aria-label={`Retirer ${member.displayName}`}
                >
                  <Trash2 size={17} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {canManage &&
        (adding ? (
          <MemberForm onAdd={addMember} onClose={() => setAdding(false)} />
        ) : (
          <button class="add-task" onClick={() => setAdding(true)}>
            <UserPlus size={18} />
            Ajouter un membre
          </button>
        ))}

      {notice && <p class="notice">{notice}</p>}

      {canManage && people.length > 0 && (
        <p class="notice muted">
          Retirer quelqu’un lui retire l’accès au foyer, mais garde ses cases cochées dans
          l’historique. Le code d’invitation arrive dans la boîte mail indiquée : si elle refuse
          les adresses avec un <strong>+</strong>, donne-lui une adresse à elle.
        </p>
      )}
    </>
  )
}
